import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useTheme } from '../../contexts/ThemeContext';
import { useAppSettings } from '../../contexts/AppSettingsContext';
import api from '../../services/api';
import { formatCurrency } from '../../utils/auth';
import {
  Box, Card, CardContent, Grid, Typography, Button,
  Paper, Divider, CircularProgress, Alert, Chip,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  IconButton
} from '@mui/material';
import {
  // Dashboard as DashboardIcon,
  Person as PersonIcon,
  Business as BusinessIcon,
  CurrencyRupee as CurrencyRupeeIcon,
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon,
  Warning as WarningIcon,
  Upload as UploadIcon,
  Visibility as VisibilityIcon
} from '@mui/icons-material';

// Helper function to format status labels
const formatStatus = (status) => {
  if (!status) return '-';
  
  // Map status to standard format
  const statusMap = {
    'normal': 'Normal',
    'over_exposure': 'Over Exposure',
    'overexposure': 'Over Exposure',
    'over exposure': 'Over Exposure',
    'defaulter': 'Defaulter',
    'blacklisted': 'Blacklisted',
    'overdue': 'Overdue'
  };
  
  // Get standardized status name and return it
  return statusMap[status.toLowerCase()] || status.charAt(0).toUpperCase() + status.slice(1);
};


// Dashboard Stat Card Component
const StatCard = ({ title, value, icon: Icon, color, onClick }) => {
  // Get current theme mode
  const { themeMode } = useTheme();
  const isDarkMode = themeMode === 'dark';
  
  // Define pastel colors for different card types
  const getPastelColor = (colorName) => {
    if (isDarkMode) {
      // Dark mode colors (higher contrast for readability)
      switch (colorName) {
        case 'primary':
          return {
            bg: 'rgba(59, 69, 105, 0.95)', // Dark blue with higher opacity
            text: '#e8e8fd', // Light text for dark background
            iconBg: 'rgba(165, 205, 255, 0.25)', // Glowing icon background
            iconColor: '#a5cdff', // Brighter icon for dark mode
            border: '1px solid rgba(165, 205, 255, 0.3)'
          };
        case 'secondary':
          return {
            bg: 'rgba(80, 49, 73, 0.95)', // Dark pink with higher opacity
            text: '#ffd6f3', // Light pink text
            iconBg: 'rgba(255, 195, 238, 0.25)', // Glowing icon background
            iconColor: '#ffc3ee', // Brighter icon for dark mode
            border: '1px solid rgba(255, 195, 238, 0.3)'
          };
        case 'success':
          return {
            bg: 'rgba(45, 82, 69, 0.95)', // Dark green with higher opacity
            text: '#d8fff0', // Light green text
            iconBg: 'rgba(196, 245, 229, 0.25)', // Glowing icon background
            iconColor: '#c4f5e5', // Brighter icon for dark mode
            border: '1px solid rgba(196, 245, 229, 0.3)'
          };
        case 'info':
          return {
            bg: 'rgba(41, 76, 91, 0.95)', // Dark blue with higher opacity
            text: '#d4f3ff', // Light blue text
            iconBg: 'rgba(180, 234, 255, 0.25)', // Glowing icon background
            iconColor: '#b4eaff', // Brighter icon for dark mode
            border: '1px solid rgba(180, 234, 255, 0.3)'
          };
        case 'warning':
          return {
            bg: 'rgba(82, 75, 35, 0.95)', // Dark yellow with higher opacity
            text: '#fffdcc', // Light yellow text
            iconBg: 'rgba(255, 253, 204, 0.25)', // Glowing icon background
            iconColor: '#fffdcc', // Brighter icon for dark mode
            border: '1px solid rgba(255, 253, 204, 0.3)'
          };
        case 'error':
          return {
            bg: 'rgba(82, 45, 48, 0.95)', // Dark red with higher opacity
            text: '#ffd4d9', // Light red text
            iconBg: 'rgba(255, 179, 186, 0.25)', // Glowing icon background
            iconColor: '#ffb3ba', // Brighter icon for dark mode
            border: '1px solid rgba(255, 179, 186, 0.3)'
          };
        default:
          return {
            bg: 'rgba(40, 42, 66, 0.95)', // Dark background
            text: '#e8e8fd', // Light text
            iconBg: 'rgba(255, 255, 255, 0.15)', // Subtle glow
            iconColor: '#b4b8e2', // Soft purple icon
            border: '1px solid rgba(255, 255, 255, 0.1)'
          };
      }
    } else {
      // Light mode pastel colors
      switch (colorName) {
        case 'primary':
          return {
            bg: 'rgba(148, 194, 253, 0.85)', // Pastel blue
            text: '#333333',
            iconBg: 'rgba(255, 255, 255, 0.85)',
            iconColor: '#4f46e5',
            border: '1px solid rgba(148, 194, 253, 0.5)'
          };
        case 'secondary':
          return {
            bg: 'rgba(255, 181, 232, 0.85)', // Pastel pink
            text: '#333333',
            iconBg: 'rgba(255, 255, 255, 0.85)',
            iconColor: '#ec4899',
            border: '1px solid rgba(255, 181, 232, 0.5)'
          };
        case 'success':
          return {
            bg: 'rgba(181, 234, 215, 0.85)', // Pastel mint
            text: '#333333',
            iconBg: 'rgba(255, 255, 255, 0.85)',
            iconColor: '#10b981',
            border: '1px solid rgba(181, 234, 215, 0.5)'
          };
        case 'info':
          return {
            bg: 'rgba(157, 227, 253, 0.85)', // Pastel light blue
            text: '#333333',
            iconBg: 'rgba(255, 255, 255, 0.85)',
            iconColor: '#0ea5e9',
            border: '1px solid rgba(157, 227, 253, 0.5)'
          };
        case 'warning':
          return {
            bg: 'rgba(253, 255, 182, 0.85)', // Pastel yellow
            text: '#333333',
            iconBg: 'rgba(255, 255, 255, 0.85)',
            iconColor: '#f59e0b',
            border: '1px solid rgba(253, 255, 182, 0.5)'
          };
        case 'error':
          return {
            bg: 'rgba(255, 154, 162, 0.85)', // Pastel salmon
            text: '#333333',
            iconBg: 'rgba(255, 255, 255, 0.85)',
            iconColor: '#ef4444',
            border: '1px solid rgba(255, 154, 162, 0.5)'
          };
        default:
          return {
            bg: 'rgba(246, 248, 255, 0.85)', // Light off-white
            text: '#333333',
            iconBg: 'rgba(255, 255, 255, 0.85)',
            iconColor: '#64748b',
            border: '1px solid rgba(187, 200, 224, 0.3)'
          };
      }
    }
  };

  const pastelColor = getPastelColor(color);

  return (
    <Card 
      className="stat-card" 
      onClick={onClick} 
      sx={{ 
        cursor: onClick ? 'pointer' : 'default',
        backgroundColor: pastelColor.bg,
        border: pastelColor.border,
        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.05)',
        transition: 'transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out',
        '&:hover': onClick ? {
          transform: 'translateY(-5px)',
          boxShadow: '0 8px 20px rgba(0, 0, 0, 0.08)',
        } : {},
      }}
    >
      <CardContent>
        <Grid container spacing={2} alignItems="center">
          <Grid item>
            <Box
              sx={{
                backgroundColor: pastelColor.iconBg,
                borderRadius: '50%',
                height: 48,
                width: 48,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                boxShadow: '0 2px 6px rgba(0, 0, 0, 0.08)',
              }}
            >
              <Icon sx={{ color: pastelColor.iconColor }} />
            </Box>
          </Grid>
          <Grid item xs>
            <Typography variant="body2" sx={{ fontWeight: 600, color: pastelColor.text, opacity: 0.8 }}>
              {title}
            </Typography>
            <Typography variant="h5" component="div" sx={{ fontWeight: 700, color: pastelColor.text }}>
              {value}
            </Typography>
          </Grid>
        </Grid>
      </CardContent>
    </Card>
  );
};

const Dashboard = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { themeMode } = useTheme();
  const { allowedStatuses } = useAppSettings();
  
  const [dashboardData, setDashboardData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  useEffect(() => {
    const fetchDashboardData = async () => {
      try {
        setLoading(true);
        setError(null);
        
        // Fetch real data from the API
        const response = await api.get('/api/reports/dashboard');
        setDashboardData(response.data);
      } catch (err) {
        console.error('Error fetching dashboard data:', err);
        setError(err.response?.data?.error || 'Failed to load dashboard data');
      } finally {
        setLoading(false);
      }
    };
    
    fetchDashboardData();
  }, [user]);
  
  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', pt: 4 }}>
        <CircularProgress />
      </Box>
    );
  }
  
  if (error) {
    return (
      <Alert severity="error" sx={{ mt: 2 }}>
        {error}
      </Alert>
    );
  }
  
  // Render different dashboard based on user role
  if (user.role === 'superadmin') {
    return (
      <Box>
        <Box className="page-title">
          <Typography variant="h4">
            SuperAdmin Dashboard
          </Typography>
        </Box>
        
        <Box sx={{ mb: 4 }}>
          <Typography variant="h5" gutterBottom>
            Overall Statistics
          </Typography>
          
          <Grid container spacing={3}>
            <Grid item xs={12} sm={6} md={3}>
              <StatCard
                title="Total Associations"
                value={dashboardData.association_stats.length}
                icon={BusinessIcon}
                color="primary"
                onClick={() => navigate('/admin/associations')}
              />
            </Grid>
            
            <Grid item xs={12} sm={6} md={3}>
              <StatCard
                title="Total Outstanding"
                value={formatCurrency(dashboardData.association_stats.reduce((sum, assoc) => sum + assoc.total_outstanding, 0))}
                icon={CurrencyRupeeIcon}
                color="secondary"
                onClick={() => navigate('/customers')}
              />
            </Grid>
            
            <Grid item xs={12} sm={6} md={3}>
              <StatCard
                title="Total Recovery"
                value={formatCurrency(dashboardData.recovery_trends.total_recovery)}
                icon={TrendingUpIcon}
                color="success"
                onClick={() => navigate('/reports/recovery-trends')}
              />
            </Grid>
            
            <Grid item xs={12} sm={6} md={3}>
              <StatCard
                title="Distributors / Viewers"
                value={`${dashboardData.user_stats.total_subadmins} / ${dashboardData.user_stats.total_viewers}`}
                icon={PersonIcon}
                color="info"
                onClick={() => navigate('/admin/users')}
              />
            </Grid>
          </Grid>
        </Box>
        
        <Box sx={{ mb: 4 }}>
          <Typography variant="h5" gutterBottom>
            Associations
          </Typography>
          
          <Grid container spacing={3}>
            {dashboardData.association_stats.map(assoc => (
              <Grid item xs={12} md={6} key={assoc.id}>
                <Paper sx={{ p: 2 }}>
                  <Typography variant="h6" gutterBottom>
                    {assoc.name}
                  </Typography>
                  
                  <Divider sx={{ my: 1 }} />
                  
                  <Grid container spacing={2}>
                    <Grid item xs={6}>
                      <Typography variant="body2" color="textSecondary">
                        Total Outstanding
                      </Typography>
                      <Typography variant="body1">
                        {formatCurrency(assoc.total_outstanding)}
                      </Typography>
                    </Grid>
                    
                    <Grid item xs={6}>
                      <Typography variant="body2" color="textSecondary">
                        Distributors / Customers
                      </Typography>
                      <Typography variant="body1">
                        {assoc.total_distributors} / {assoc.total_customers}
                      </Typography>
                    </Grid>
                    
                    <Grid item xs={12}>
                      <Typography variant="body2" color="textSecondary" gutterBottom>
                        Customer Status
                      </Typography>
                      <Box sx={{ display: 'flex', gap: 1 }}>
                        <Chip 
                          label={`Normal: ${assoc.status_counts.normal}`} 
                          size="small" 
                          variant="filled"
                          sx={{
                            bgcolor: 'success.main',
                            color: 'success.contrastText',
                            '& .MuiChip-label': {
                              color: 'success.contrastText',
                            }
                          }}
                        />
                        <Chip 
                          label={`Defaulter: ${assoc.status_counts.defaulter}`} 
                          size="small" 
                          variant="filled"
                          sx={{
                            bgcolor: 'warning.main',
                            color: 'warning.contrastText',
                            '& .MuiChip-label': {
                              color: 'warning.contrastText',
                            }
                          }}
                        />
                      </Box>
                    </Grid>
                  </Grid>
                  
                  <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end' }}>
                    <Button 
                      size="small" 
                      onClick={() => navigate(`/admin/associations/${assoc.id}/edit`)}
                    >
                      View Details
                    </Button>
                  </Box>
                </Paper>
              </Grid>
            ))}
          </Grid>
        </Box>
        
        <Box>
          <Typography variant="h5" gutterBottom>
            Recovery Trends
          </Typography>
          
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <Paper sx={{ p: 2 }}>
                <Typography variant="h6" gutterBottom>
                  Top Improving Distributors
                </Typography>
                
                <Divider sx={{ my: 1 }} />
                
                {dashboardData.recovery_trends.improving_distributors.map(dist => (
                  <Box key={dist.id} sx={{ display: 'flex', justifyContent: 'space-between', py: 1 }}>
                    <Typography variant="body1">
                      {dist.name}
                    </Typography>
                    <Typography variant="body1" color="success.main">
                      {formatCurrency(dist.amount_decreased)}
                    </Typography>
                  </Box>
                ))}
                
                <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end' }}>
                  <Button 
                    size="small" 
                    onClick={() => navigate('/reports/recovery-trends')}
                  >
                    View All
                  </Button>
                </Box>
              </Paper>
            </Grid>
            
            <Grid item xs={12} md={6}>
              <Paper sx={{ p: 2 }}>
                <Typography variant="h6" gutterBottom>
                  Top Worsening Distributors
                </Typography>
                
                <Divider sx={{ my: 1 }} />
                
                {dashboardData.recovery_trends.worsening_distributors.map(dist => (
                  <Box key={dist.id} sx={{ display: 'flex', justifyContent: 'space-between', py: 1 }}>
                    <Typography variant="body1">
                      {dist.name}
                    </Typography>
                    <Typography variant="body1" color="error.main">
                      {formatCurrency(dist.amount_increased)}
                    </Typography>
                  </Box>
                ))}
                
                <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end' }}>
                  <Button 
                    size="small" 
                    onClick={() => navigate('/admin/users')}
                  >
                    View All Distributors
                  </Button>
                </Box>
              </Paper>
            </Grid>
          </Grid>
        </Box>
      </Box>
    );
  } else if (user.role === 'subadmin' && user.is_association_admin) {
    return (
      <Box>
        <Box className="page-title">
          <Typography variant="h4">
            Association Admin Dashboard: {dashboardData.association.name}
          </Typography>
          
          {user.role === 'subadmin' && (
            <Button
              variant="contained"
              startIcon={<UploadIcon />}
              onClick={() => navigate('/uploads/outstanding')}
            >
              Upload Outstanding
            </Button>
          )}
        </Box>
        
        {/* Regular SubAdmin Stats with additional Association Admin Metrics */}
        <Box sx={{ mb: 4 }}>
          <Typography variant="h5" gutterBottom>
            Overview
          </Typography>
          
          <Grid container spacing={3}>
            {/* Regular SubAdmin Stats (same as normal subadmin) */}
            <Grid item xs={12} sm={6} md={3}>
              <StatCard
                title="Your Outstanding"
                value={formatCurrency(dashboardData.overview.user_outstanding)}
                icon={CurrencyRupeeIcon}
                color="primary"
                onClick={() => navigate('/customers')}
              />
            </Grid>
            
            <Grid item xs={12} sm={6} md={3}>
              <StatCard
                title="Association Outstanding"
                value={formatCurrency(dashboardData.overview.association_outstanding)}
                icon={CurrencyRupeeIcon}
                color="info"
              />
            </Grid>
            
            <Grid item xs={12} sm={6} md={3}>
              <StatCard
                title="Customer Count"
                value={dashboardData.overview.customers_with_outstanding}
                icon={PersonIcon}
                color="secondary"
                onClick={() => navigate('/customers')}
              />
            </Grid>
            
            <Grid item xs={12} sm={6} md={3}>
              <StatCard
                title="Net Recovery"
                value={formatCurrency(Math.abs(dashboardData.recovery_trends.net_change))}
                icon={TrendingUpIcon}
                color="success"
              />
            </Grid>
            
            {/* Association Admin specific stats */}
            <Grid item xs={12} sm={6} md={3}>
              <StatCard
                title="Total Distributors"
                value={dashboardData.overview.total_distributors}
                icon={BusinessIcon}
                color="secondary"
                onClick={() => navigate('/admin/users')}
              />
            </Grid>
            
            <Grid item xs={12} sm={6} md={3}>
              <StatCard
                title="Pending Status Changes"
                value={dashboardData.overview.pending_status_changes}
                icon={WarningIcon}
                color="warning"
                onClick={() => navigate('/status-changes')}
              />
            </Grid>
          </Grid>
        </Box>
        
        {/* Customer Status Percentage based on allowed statuses */}
        <Box sx={{ mb: 4 }}>
          <Typography variant="h5" gutterBottom>
            Customer Status Percentage
          </Typography>
          
          <Grid container spacing={2}>
            {/* Dynamically render status cards based on allowed statuses */}
            {dashboardData.association && dashboardData.association.allowed_status ? (
              // If association data with allowed_status is available
              dashboardData.association.allowed_status.map((status, index) => {
                const statusKey = status.toLowerCase().replace(/ /g, '_');
                const percentage = dashboardData.overview.status_percentage[statusKey] || 0;
                
                // Determine color scheme based on status
                let colorScheme;
                if (status === 'Normal') {
                  colorScheme = {
                    bg: themeMode === 'dark' ? 'rgba(45, 82, 69, 0.95)' : 'rgba(181, 234, 215, 0.85)',
                    border: themeMode === 'dark' ? '1px solid rgba(196, 245, 229, 0.3)' : '1px solid rgba(181, 234, 215, 0.5)',
                    text: themeMode === 'dark' ? '#d8fff0' : '#333333'
                  };
                } else if (status === 'Defaulter') {
                  colorScheme = {
                    bg: themeMode === 'dark' ? 'rgba(82, 75, 35, 0.95)' : 'rgba(253, 255, 182, 0.85)',
                    border: themeMode === 'dark' ? '1px solid rgba(255, 253, 204, 0.3)' : '1px solid rgba(253, 255, 182, 0.5)',
                    text: themeMode === 'dark' ? '#fffdcc' : '#333333'
                  };
                } else if (status === 'Overdue') {
                  colorScheme = {
                    bg: themeMode === 'dark' ? 'rgba(80, 78, 48, 0.95)' : 'rgba(255, 248, 179, 0.85)',
                    border: themeMode === 'dark' ? '1px solid rgba(255, 248, 179, 0.3)' : '1px solid rgba(255, 248, 179, 0.5)',
                    text: themeMode === 'dark' ? '#fffcdd' : '#333333'
                  };
                } else if (status === 'Over Exposure') {
                  colorScheme = {
                    bg: themeMode === 'dark' ? 'rgba(41, 76, 91, 0.95)' : 'rgba(180, 234, 255, 0.85)',
                    border: themeMode === 'dark' ? '1px solid rgba(180, 234, 255, 0.3)' : '1px solid rgba(180, 234, 255, 0.5)',
                    text: themeMode === 'dark' ? '#d4f3ff' : '#333333'
                  };
                } else if (status === 'Blacklisted') {
                  colorScheme = {
                    bg: themeMode === 'dark' ? 'rgba(82, 45, 48, 0.95)' : 'rgba(255, 179, 186, 0.85)',
                    border: themeMode === 'dark' ? '1px solid rgba(255, 179, 186, 0.3)' : '1px solid rgba(255, 179, 186, 0.5)',
                    text: themeMode === 'dark' ? '#ffd4d9' : '#333333'
                  };
                } else {
                  // Default color scheme
                  colorScheme = {
                    bg: themeMode === 'dark' ? 'rgba(59, 69, 105, 0.95)' : 'rgba(148, 194, 253, 0.85)',
                    border: themeMode === 'dark' ? '1px solid rgba(165, 205, 255, 0.3)' : '1px solid rgba(148, 194, 253, 0.5)',
                    text: themeMode === 'dark' ? '#e8e8fd' : '#333333'
                  };
                }
                
                return (
                  <Grid item xs={12} sm={Math.max(6, 12 / dashboardData.association.allowed_status.length)} key={status}>
                    <Paper sx={{ 
                      p: 2, 
                      backgroundColor: colorScheme.bg,
                      border: colorScheme.border,
                      boxShadow: '0 4px 12px rgba(0, 0, 0, 0.05)',
                      textAlign: 'center'
                    }}>
                      <Box sx={{ textAlign: 'center', p: 1 }}>
                        <Typography variant="h3" sx={{ 
                          color: colorScheme.text, 
                          fontWeight: 700 
                        }}>
                          {percentage}%
                        </Typography>
                        <Typography variant="body1" sx={{ 
                          color: colorScheme.text, 
                          fontWeight: 600 
                        }}>
                          {status}
                        </Typography>
                      </Box>
                    </Paper>
                  </Grid>
                );
              })
            ) : (
              // Fallback to default Normal and Defaulter if no allowed_status is available
              <>
                <Grid item xs={12} sm={6}>
                  <Paper sx={{ 
                    p: 2, 
                    backgroundColor: themeMode === 'dark' 
                      ? 'rgba(45, 82, 69, 0.95)' // Dark green with higher opacity
                      : 'rgba(181, 234, 215, 0.85)', // Pastel mint
                    border: themeMode === 'dark'
                      ? '1px solid rgba(196, 245, 229, 0.3)'
                      : '1px solid rgba(181, 234, 215, 0.5)',
                    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.05)',
                    textAlign: 'center'
                  }}>
                    <Box sx={{ textAlign: 'center', p: 1 }}>
                      <Typography variant="h3" sx={{ 
                        color: themeMode === 'dark' ? '#d8fff0' : '#333333', 
                        fontWeight: 700 
                      }}>
                        {dashboardData.overview.status_percentage.normal || 0}%
                      </Typography>
                      <Typography variant="body1" sx={{ 
                        color: themeMode === 'dark' ? '#d8fff0' : '#333333', 
                        fontWeight: 600 
                      }}>
                        Normal
                      </Typography>
                    </Box>
                  </Paper>
                </Grid>
                
                <Grid item xs={12} sm={6}>
                  <Paper sx={{ 
                    p: 2, 
                    backgroundColor: themeMode === 'dark' 
                      ? 'rgba(82, 75, 35, 0.95)' // Dark yellow with higher opacity
                      : 'rgba(253, 255, 182, 0.85)', // Pastel yellow
                    border: themeMode === 'dark'
                      ? '1px solid rgba(255, 253, 204, 0.3)'
                      : '1px solid rgba(253, 255, 182, 0.5)',
                    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.05)',
                    textAlign: 'center'
                  }}>
                    <Box sx={{ textAlign: 'center', p: 1 }}>
                      <Typography variant="h3" sx={{ 
                        color: themeMode === 'dark' ? '#fffdcc' : '#333333', 
                        fontWeight: 700 
                      }}>
                        {dashboardData.overview.status_percentage.defaulter || 0}%
                      </Typography>
                      <Typography variant="body1" sx={{ 
                        color: themeMode === 'dark' ? '#fffdcc' : '#333333', 
                        fontWeight: 600 
                      }}>
                        Defaulter
                      </Typography>
                    </Box>
                  </Paper>
                </Grid>
              </>
            )}
          </Grid>
        </Box>
        
        {/* Top Customers - same as regular subadmin */}
        <Box>
          <Typography variant="h5" gutterBottom>
            Top Customers by Outstanding
          </Typography>
          
          {dashboardData.top_customers && dashboardData.top_customers.length > 0 ? (
            <Paper>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Customer Name</TableCell>
                    <TableCell>GSTIN</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell align="right">Outstanding Amount</TableCell>
                    <TableCell>Action</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {dashboardData.top_customers.map(customer => (
                    <TableRow key={customer.id}>
                      <TableCell>{customer.name}</TableCell>
                      <TableCell>{customer.gstin || '-'}</TableCell>
                      <TableCell>
                        <Chip 
                          label={customer.status_display || formatStatus(customer.status)} 
                          size="small" 
                          variant="filled"
                          sx={{
                            ...((customer.status_display || '').toLowerCase() === 'normal' || customer.status.toLowerCase() === 'normal' && {
                              bgcolor: 'success.main',
                              color: 'success.contrastText',
                              '& .MuiChip-label': {
                                color: 'success.contrastText',
                              }
                            }),
                            ...((customer.status_display || '').toLowerCase() === 'defaulter' || customer.status.toLowerCase() === 'defaulter' && {
                              bgcolor: 'warning.main',
                              color: 'warning.contrastText',
                              '& .MuiChip-label': {
                                color: 'warning.contrastText',
                              }
                            }),
                            ...((customer.status_display || '').toLowerCase() === 'overdue' || customer.status.toLowerCase() === 'overdue' && {
                              bgcolor: 'error.light',
                              color: 'error.contrastText',
                              '& .MuiChip-label': {
                                color: 'error.contrastText',
                              }
                            }),
                            ...((customer.status_display || '').toLowerCase() === 'over exposure' || 
                                ['over exposure', 'over_exposure', 'overexposure'].includes(customer.status.toLowerCase()) && {
                              bgcolor: 'info.main',
                              color: 'info.contrastText',
                              '& .MuiChip-label': {
                                color: 'info.contrastText',
                              }
                            }),
                            ...((customer.status_display || '').toLowerCase() === 'blacklisted' || customer.status.toLowerCase() === 'blacklisted' && {
                              bgcolor: 'error.main',
                              color: 'error.contrastText',
                              '& .MuiChip-label': {
                                color: 'error.contrastText',
                              }
                            })
                          }}
                        />
                      </TableCell>
                      <TableCell align="right">{formatCurrency(customer.amount)}</TableCell>
                      <TableCell>
                        <IconButton 
                          size="small" 
                          color="primary" 
                          onClick={() => navigate(`/customers/${customer.id}`)}
                        >
                          <VisibilityIcon />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Paper>
          ) : (
            <Paper sx={{ p: 3, textAlign: 'center' }}>
              <Typography variant="body1" color="textSecondary">
                No customer data available.
              </Typography>
            </Paper>
          )}
          
          <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end' }}>
            <Button 
              size="small" 
              onClick={() => navigate('/customers')}
            >
              View All Customers
            </Button>
          </Box>
        </Box>
      </Box>
    );
  } else {
    return (
      <Box>
        <Box className="page-title">
          <Typography variant="h4">
            Distributor Dashboard
          </Typography>
          
          {user.role === 'subadmin' && (
            <Button
              variant="contained"
              startIcon={<UploadIcon />}
              onClick={() => navigate('/uploads/outstanding')}
            >
              Upload Outstanding
            </Button>
          )}
        </Box>
        
        <Box sx={{ mb: 4 }}>
          <Typography variant="h5" gutterBottom>
            Your Overview
          </Typography>
          
          <Grid container spacing={3}>
            <Grid item xs={12} sm={6} md={3}>
              <StatCard
                title="Your Outstanding"
                value={formatCurrency(dashboardData.overview.user_outstanding)}
                icon={CurrencyRupeeIcon}
                color="primary"
                onClick={() => navigate('/customers')}
              />
            </Grid>
            
            <Grid item xs={12} sm={6} md={3}>
              <StatCard
                title="Association Outstanding"
                value={formatCurrency(dashboardData.overview.association_outstanding)}
                icon={CurrencyRupeeIcon}
                color="info"
              />
            </Grid>
            
            <Grid item xs={12} sm={6} md={3}>
              <StatCard
                title="Customer Count"
                value={dashboardData.overview.customers_with_outstanding}
                icon={PersonIcon}
                color="secondary"
                onClick={() => navigate('/customers')}
              />
            </Grid>
            
            <Grid item xs={12} sm={6} md={3}>
              <StatCard
                title="Net Recovery"
                value={formatCurrency(Math.abs(dashboardData.recovery_trends.net_change))}
                icon={TrendingUpIcon}
                color="success"
              />
            </Grid>
          </Grid>
        </Box>
        
        <Box sx={{ mb: 4 }}>
          <Typography variant="h5" gutterBottom>
            Customer Status Percentage
          </Typography>
          
          <Grid container spacing={2}>
            {/* Dynamically render status cards based on allowed statuses */}
            {dashboardData.association && dashboardData.association.allowed_status ? (
              // If dashboardData includes allowed_status from association
              dashboardData.association.allowed_status.map((status, index) => {
                const statusKey = status.toLowerCase().replace(/ /g, '_');
                const percentage = dashboardData.overview.status_percentage[statusKey] || 0;
                
                // Determine color scheme based on status
                let colorScheme;
                if (status === 'Normal') {
                  colorScheme = {
                    bg: themeMode === 'dark' ? 'rgba(45, 82, 69, 0.95)' : 'rgba(181, 234, 215, 0.85)',
                    border: themeMode === 'dark' ? '1px solid rgba(196, 245, 229, 0.3)' : '1px solid rgba(181, 234, 215, 0.5)',
                    text: themeMode === 'dark' ? '#d8fff0' : '#333333'
                  };
                } else if (status === 'Defaulter') {
                  colorScheme = {
                    bg: themeMode === 'dark' ? 'rgba(82, 75, 35, 0.95)' : 'rgba(253, 255, 182, 0.85)',
                    border: themeMode === 'dark' ? '1px solid rgba(255, 253, 204, 0.3)' : '1px solid rgba(253, 255, 182, 0.5)',
                    text: themeMode === 'dark' ? '#fffdcc' : '#333333'
                  };
                } else if (status === 'Overdue') {
                  colorScheme = {
                    bg: themeMode === 'dark' ? 'rgba(80, 78, 48, 0.95)' : 'rgba(255, 248, 179, 0.85)',
                    border: themeMode === 'dark' ? '1px solid rgba(255, 248, 179, 0.3)' : '1px solid rgba(255, 248, 179, 0.5)',
                    text: themeMode === 'dark' ? '#fffcdd' : '#333333'
                  };
                } else if (status === 'Over Exposure') {
                  colorScheme = {
                    bg: themeMode === 'dark' ? 'rgba(41, 76, 91, 0.95)' : 'rgba(180, 234, 255, 0.85)',
                    border: themeMode === 'dark' ? '1px solid rgba(180, 234, 255, 0.3)' : '1px solid rgba(180, 234, 255, 0.5)',
                    text: themeMode === 'dark' ? '#d4f3ff' : '#333333'
                  };
                } else if (status === 'Blacklisted') {
                  colorScheme = {
                    bg: themeMode === 'dark' ? 'rgba(82, 45, 48, 0.95)' : 'rgba(255, 179, 186, 0.85)',
                    border: themeMode === 'dark' ? '1px solid rgba(255, 179, 186, 0.3)' : '1px solid rgba(255, 179, 186, 0.5)',
                    text: themeMode === 'dark' ? '#ffd4d9' : '#333333'
                  };
                } else {
                  // Default color scheme
                  colorScheme = {
                    bg: themeMode === 'dark' ? 'rgba(59, 69, 105, 0.95)' : 'rgba(148, 194, 253, 0.85)',
                    border: themeMode === 'dark' ? '1px solid rgba(165, 205, 255, 0.3)' : '1px solid rgba(148, 194, 253, 0.5)',
                    text: themeMode === 'dark' ? '#e8e8fd' : '#333333'
                  };
                }
                
                return (
                  <Grid item xs={12} sm={Math.max(6, 12 / dashboardData.association.allowed_status.length)} key={status}>
                    <Paper sx={{ 
                      p: 2, 
                      backgroundColor: colorScheme.bg,
                      border: colorScheme.border,
                      boxShadow: '0 4px 12px rgba(0, 0, 0, 0.05)',
                      textAlign: 'center'
                    }}>
                      <Box sx={{ textAlign: 'center', p: 1 }}>
                        <Typography variant="h3" sx={{ 
                          color: colorScheme.text, 
                          fontWeight: 700 
                        }}>
                          {percentage}%
                        </Typography>
                        <Typography variant="body1" sx={{ 
                          color: colorScheme.text, 
                          fontWeight: 600 
                        }}>
                          {status}
                        </Typography>
                      </Box>
                    </Paper>
                  </Grid>
                );
              })
            ) : (
              // Fallback to default Normal and Defaulter if no allowed_status is available
              <>
                <Grid item xs={12} sm={6}>
                  <Paper sx={{ 
                    p: 2, 
                    backgroundColor: themeMode === 'dark' 
                      ? 'rgba(45, 82, 69, 0.95)' // Dark green with higher opacity
                      : 'rgba(181, 234, 215, 0.85)', // Pastel mint
                    border: themeMode === 'dark'
                      ? '1px solid rgba(196, 245, 229, 0.3)'
                      : '1px solid rgba(181, 234, 215, 0.5)',
                    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.05)',
                    textAlign: 'center'
                  }}>
                    <Box sx={{ textAlign: 'center', p: 1 }}>
                      <Typography variant="h3" sx={{ 
                        color: themeMode === 'dark' ? '#d8fff0' : '#333333', 
                        fontWeight: 700 
                      }}>
                        {dashboardData.overview.status_percentage.normal || 0}%
                      </Typography>
                      <Typography variant="body1" sx={{ 
                        color: themeMode === 'dark' ? '#d8fff0' : '#333333', 
                        fontWeight: 600 
                      }}>
                        Normal
                      </Typography>
                    </Box>
                  </Paper>
                </Grid>
                
                <Grid item xs={12} sm={6}>
                  <Paper sx={{ 
                    p: 2, 
                    backgroundColor: themeMode === 'dark' 
                      ? 'rgba(82, 75, 35, 0.95)' // Dark yellow with higher opacity
                      : 'rgba(253, 255, 182, 0.85)', // Pastel yellow
                    border: themeMode === 'dark'
                      ? '1px solid rgba(255, 253, 204, 0.3)'
                      : '1px solid rgba(253, 255, 182, 0.5)',
                    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.05)',
                    textAlign: 'center'
                  }}>
                    <Box sx={{ textAlign: 'center', p: 1 }}>
                      <Typography variant="h3" sx={{ 
                        color: themeMode === 'dark' ? '#fffdcc' : '#333333', 
                        fontWeight: 700 
                      }}>
                        {dashboardData.overview.status_percentage.defaulter || 0}%
                      </Typography>
                      <Typography variant="body1" sx={{ 
                        color: themeMode === 'dark' ? '#fffdcc' : '#333333', 
                        fontWeight: 600 
                      }}>
                        Defaulter
                      </Typography>
                    </Box>
                  </Paper>
                </Grid>
              </>
            )}
          </Grid>
        </Box>
        
        <Box>
          <Typography variant="h5" gutterBottom>
            Top Customers by Outstanding
          </Typography>
          
          {dashboardData.top_customers && dashboardData.top_customers.length > 0 ? (
            <Paper>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Customer Name</TableCell>
                    <TableCell>GSTIN</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell align="right">Outstanding Amount</TableCell>
                    <TableCell>Action</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {dashboardData.top_customers.map(customer => (
                    <TableRow key={customer.id}>
                      <TableCell>{customer.name}</TableCell>
                      <TableCell>{customer.gstin || '-'}</TableCell>
                      <TableCell>
                        <Chip 
                          label={customer.status_display || formatStatus(customer.status)} 
                          size="small" 
                          variant="filled"
                          sx={{
                            ...((customer.status_display || '').toLowerCase() === 'normal' || customer.status.toLowerCase() === 'normal' && {
                              bgcolor: 'success.main',
                              color: 'success.contrastText',
                              '& .MuiChip-label': {
                                color: 'success.contrastText',
                              }
                            }),
                            ...((customer.status_display || '').toLowerCase() === 'defaulter' || customer.status.toLowerCase() === 'defaulter' && {
                              bgcolor: 'warning.main',
                              color: 'warning.contrastText',
                              '& .MuiChip-label': {
                                color: 'warning.contrastText',
                              }
                            }),
                            ...((customer.status_display || '').toLowerCase() === 'overdue' || customer.status.toLowerCase() === 'overdue' && {
                              bgcolor: 'error.light',
                              color: 'error.contrastText',
                              '& .MuiChip-label': {
                                color: 'error.contrastText',
                              }
                            }),
                            ...((customer.status_display || '').toLowerCase() === 'over exposure' || 
                                ['over exposure', 'over_exposure', 'overexposure'].includes(customer.status.toLowerCase()) && {
                              bgcolor: 'info.main',
                              color: 'info.contrastText',
                              '& .MuiChip-label': {
                                color: 'info.contrastText',
                              }
                            }),
                            ...((customer.status_display || '').toLowerCase() === 'blacklisted' || customer.status.toLowerCase() === 'blacklisted' && {
                              bgcolor: 'error.main',
                              color: 'error.contrastText',
                              '& .MuiChip-label': {
                                color: 'error.contrastText',
                              }
                            })
                          }}
                        />
                      </TableCell>
                      <TableCell align="right">{formatCurrency(customer.amount)}</TableCell>
                      <TableCell>
                        <IconButton 
                          size="small" 
                          color="primary" 
                          onClick={() => navigate(`/customers/${customer.id}`)}
                        >
                          <VisibilityIcon />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Paper>
          ) : (
            <Paper sx={{ p: 3, textAlign: 'center' }}>
              <Typography variant="body1" color="textSecondary">
                No customer data available.
              </Typography>
            </Paper>
          )}
          
          <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end' }}>
            <Button 
              size="small" 
              onClick={() => navigate('/customers')}
            >
              View All Customers
            </Button>
          </Box>
        </Box>
      </Box>
    );
  }
};

export default Dashboard;